import { Fragment } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal } from '@givebutter/util';
import { faFileExport } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { generateExports } from 'api/contacts/exports';
import { UseFormLabel, UseFormSubmitButton } from 'components/form';
import * as Form from 'components/form';
import { GivebutterFormProvider } from 'components/form/GivebutterFormProvider';
import { useGivebutterForm } from 'components/form/useGivebutterForm';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { YEAR_OPTIONS } from 'helpers';
import { accountQueryKeys } from 'queries/account-query-keys';
import { OutboundFilters } from 'types/request';

interface ExportContactTransactionsReportModalProps extends AlertDialog.AlertDialogRootProps {
    onSuccess?: () => void;
    onClose?: () => void;
    contactId?: number;
}

interface ReportsFormValues {
    reportSelectedYear: string;
}

const exportAllTransactionsOption = 'All transactions';

const ExportContactTransactionsReportModal = ({
    open,
    onClose,
    onSuccess,
    contactId,
    ...rest
}: ExportContactTransactionsReportModalProps) => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const methods = useGivebutterForm<ReportsFormValues>({
        defaultValues: {
            reportSelectedYear: exportAllTransactionsOption,
        },
    });
    const { data: contact } = useCurrentContact();
    const currencySymbol = useCurrencySymbol();
    const { handleSubmit, control } = methods;
    const queryClient = useQueryClient();
    const defaultPayload = {
        scope: 'all',
        object: 'account',
        report_type: 'csv',
        resource: 'transactions',
        object_id: accountId,
        filters: {
            contacts: contactId,
        },
    };

    const { mutate: handleExport, isPending: isGeneratingReport } = useMutation({
        mutationFn: (params: OutboundFilters) =>
            generateExports(accountId, {
                ...defaultPayload,
                filters: {
                    ...defaultPayload.filters,
                    ...params,
                },
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            onSuccess?.();
            onClose?.();
        },
        onError: () => {
            toast.error(
                <Fragment>
                    <span className="font-medium">Whoops.</span> Something went wrong. Please try
                    again.
                </Fragment>,
            );
        },
    });

    const onExportClick = ({ reportSelectedYear }: ReportsFormValues) => {
        let params: OutboundFilters = [];

        toast.info(
            <Fragment>
                <span className="font-medium">Processing your report!</span> It should be available
                soon.
            </Fragment>,
        );

        if (reportSelectedYear !== exportAllTransactionsOption) {
            const convertedYear = Number(reportSelectedYear);
            params.push({
                type: 'or',
                children: {
                    created_at: [
                        {
                            value: [`${convertedYear}-01-01`, `${convertedYear + 1}-01-01`],
                            type: 'and',
                            operator: 'between',
                        },
                    ],
                },
            });
        }

        handleExport(params);
    };

    return (
        <GivebutterFormProvider {...methods}>
            <AlertDialog.Root open={open} {...rest}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay />
                    <AlertDialog.Content className={'w-96'} onEscapeKeyDown={onClose}>
                        <AlertDialog.Header>
                            <div
                                className={
                                    'flex h-12 w-12 flex-col items-center justify-center rounded-full bg-gray-100 text-center align-middle text-gray-500'
                                }
                            >
                                <FontAwesomeIcon
                                    className={'flex h-5 w-5'}
                                    icon={faFileExport}
                                    fill={'text-gray-500'}
                                />
                            </div>
                            <AlertDialog.Close onClick={onClose} />
                        </AlertDialog.Header>
                        <AlertDialog.Body>
                            <form
                                onSubmit={handleSubmit(onExportClick)}
                                className="flex flex-col gap-8"
                            >
                                <div>
                                    <AlertDialog.Title>Export transactions</AlertDialog.Title>
                                    <AlertDialog.Description>
                                        Click below to start your CSV export. We’ll email you when
                                        it’s complete.
                                    </AlertDialog.Description>
                                </div>
                                <div>
                                    <UseFormLabel name={'reportSelectedYear'}>
                                        Export options
                                    </UseFormLabel>
                                    <Form.SingleSelect control={control} name="reportSelectedYear">
                                        <SingleSelect.Trigger aria-label="Selected year" />
                                        <SingleSelect.Content>
                                            <SingleSelect.Item value={exportAllTransactionsOption}>
                                                <span className="font-medium">
                                                    <SingleSelect.ItemText>
                                                        All transactions
                                                    </SingleSelect.ItemText>
                                                </span>
                                                <span className="font-thin text-gray-600">
                                                    {currencySymbol}
                                                    {maskDecimal(
                                                        (contact.stats?.contributions ?? 0) / 100,
                                                    )}{' '}
                                                    total
                                                </span>
                                            </SingleSelect.Item>
                                            {YEAR_OPTIONS.map(({ value, label }) => {
                                                if (contact.years_transacted?.[value]) {
                                                    return (
                                                        <SingleSelect.Item
                                                            key={value}
                                                            value={value.toString()}
                                                        >
                                                            <span className="font-medium">
                                                                <SingleSelect.ItemText>
                                                                    {label}
                                                                </SingleSelect.ItemText>
                                                            </span>
                                                            <span className="font-thin text-gray-600">
                                                                {currencySymbol}
                                                                {maskDecimal(
                                                                    contact.years_transacted?.[
                                                                        value
                                                                    ],
                                                                )}{' '}
                                                                total
                                                            </span>
                                                        </SingleSelect.Item>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                </div>
                                <div className="flex flex-row items-stretch gap-4">
                                    <AlertDialog.Cancel
                                        className="flex-1 items-center"
                                        onClick={onClose}
                                    >
                                        Close
                                    </AlertDialog.Cancel>
                                    <UseFormSubmitButton
                                        type="submit"
                                        className={'h-10 flex-1'}
                                        disabled={isGeneratingReport}
                                    >
                                        Export
                                    </UseFormSubmitButton>
                                </div>
                            </form>
                        </AlertDialog.Body>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </GivebutterFormProvider>
    );
};

const ExportContactTransactionsReportModalTrigger = AlertDialog.Trigger;

const Root = ExportContactTransactionsReportModal;
const Trigger = ExportContactTransactionsReportModalTrigger;

export {
    ExportContactTransactionsReportModal,
    ExportContactTransactionsReportModalTrigger,
    //
    Root,
    Trigger,
};
export type { ExportContactTransactionsReportModalProps };
